import { useState } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Mail, CheckCircle2, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent successfully!");
      setForm({ name: "", email: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            Contact Us
          </p>
          <h1
            data-testid="contact-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl"
          >
            Get in Touch
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Form */}
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1C2E4A] mb-2">
              Send Us a Message
            </h2>
            <p className="text-sm text-[#595959] mb-8">
              We would love to hear from you. Fill out the form below and we will
              get back to you as soon as possible.
            </p>

            {sent ? (
              <div
                data-testid="contact-success"
                className="bg-[#8F9779]/10 rounded-xl border border-[#8F9779]/30 p-8 text-center"
              >
                <CheckCircle2 className="w-10 h-10 text-[#8F9779] mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-2">
                  Message Sent
                </h3>
                <p className="text-sm text-[#595959]">
                  Thank you for reaching out. We will respond shortly.
                </p>
                <button
                  data-testid="send-another-btn"
                  className="mt-4 text-sm text-[#C8A97E] hover:underline font-medium"
                  onClick={() => setSent(false)}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-[#1A1A1A] text-sm font-medium mb-1.5 block">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="border-[#E5E2DC] bg-[#FAF9F6] focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A]"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#1A1A1A] text-sm font-medium mb-1.5 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="border-[#E5E2DC] bg-[#FAF9F6] focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A]"
                  />
                </div>
                <div>
                  <Label htmlFor="interest" className="text-[#1A1A1A] text-sm font-medium mb-1.5 block">
                    Area of Interest
                  </Label>
                  <Select
                    value={form.interest}
                    onValueChange={(val) => setForm({ ...form, interest: val })}
                  >
                    <SelectTrigger
                      data-testid="contact-interest-select"
                      className="border-[#E5E2DC] bg-[#FAF9F6] focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A]"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="church-school">Church School Program</SelectItem>
                      <SelectItem value="start-school">Starting a Church School</SelectItem>
                      <SelectItem value="homeschool">Homeschool Support</SelectItem>
                      <SelectItem value="general">General Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message" className="text-[#1A1A1A] text-sm font-medium mb-1.5 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    placeholder="How can we help you?"
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="border-[#E5E2DC] bg-[#FAF9F6] focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A] resize-none"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="bg-[#1C2E4A] text-white hover:bg-[#152238] px-8 py-3 rounded-md transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="lg:pt-12">
            <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-8 lg:p-10">
              <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-6">
                Contact Information
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1C2E4A]/5 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-[#1C2E4A]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A] mb-0.5">
                      Email
                    </p>
                    <a
                      href="mailto:acsvirtualchurch@gmail.com"
                      data-testid="contact-email-link"
                      className="text-sm text-[#C8A97E] hover:underline"
                    >
                      acsvirtualchurch@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1C2E4A]/5 flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4 text-[#1C2E4A]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A] mb-0.5">
                      Phone
                    </p>
                    <a
                      href="tel:+13345533444"
                      data-testid="contact-phone-link"
                      className="text-sm text-[#C8A97E] hover:underline"
                    >
                      (334) 553-3444
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1C2E4A]/5 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-[#1C2E4A]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A] mb-0.5">
                      Mailing Address
                    </p>
                    <p className="text-sm text-[#595959]">
                      833 Ewell Road<br />Slocomb, AL 36375
                    </p>
                    <p className="text-xs text-[#595959]/70 mt-1 italic">
                      We are a virtual church — this is our mailing address only.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-[#E5E2DC]">
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-3">
                  Response Time
                </h4>
                <p className="text-sm text-[#595959] leading-relaxed">
                  We typically respond within 1-2 business days. For urgent
                  matters, please indicate so in your message.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
